import { Spinner, Stack } from 'component-library';
import { Progress } from 'component-library/Progress/Progress';
import { ErrorBoundary } from 'react-error-boundary';
import { Trans, useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components';
import { Card } from '@/components/widgets/o/Card/Card';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useWorkstreams } from '@/hooks/useWorkstreams';
import { useUser } from '@/lib/auth';
import { Logger } from '@/lib/logs/logger';

import { getWorkstreamMilestones, getWorkstreamProgress } from '../utils/index';

export const UserGreetings = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-8',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('shared.home.userGreeting.overallProgress')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`CTACard rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain className='rounded-3xl bg-bg-light-grey p-8'>
            <Spinner size={10} />
          </Stack>
        }
      >
        <UserGreetingsBase />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const UserGreetingsBase = () => {
  const { t } = useTranslation();
  const workstreams = useWorkstreams().data;
  const activeWorkstreams = workstreams.length;

  const overallProgress = parseFloat(
    getDisplayNumber(
      activeWorkstreams > 0
        ? Math.round(workstreams.reduce((sum, ws) => sum + getWorkstreamProgress(ws), 0) / activeWorkstreams)
        : 0,
    ),
  );

  return (
    <Stack className='rounded-3xl border border-neutral-black-8 bg-bg-light-grey p-8' direction='row' spacing={6}>
      <Greeting />
      <Stack className='items-end justify-end w-1/3'>
        <Stack className='rounded-2xl bg-white-100 border border-neutral-black-8 p-5 w-full max-w-[250px]' spacing={3}>
          <span className='typography-overline text-text-secondary uppercase w-full'>
            {t('shared.home.userGreeting.overallProgress')}
          </span>
          <span className='typography-display3 text-primary-100'>{overallProgress}%</span>
          <Progress value={overallProgress} className='w-full bg-neutral-black-12' />
        </Stack>
      </Stack>
    </Stack>
  );
};

const Greeting = () => {
  const { t } = useTranslation();
  const user = useUser();

  const workstreams = useWorkstreams().data;
  const activeWorkstreams = workstreams.length;

  const { completedMilestones, totalMilestones } = workstreams.reduce(
    (acc, workstream) => {
      const { completed, total } = getWorkstreamMilestones(workstream);
      return {
        completedMilestones: acc.completedMilestones + completed,
        totalMilestones: acc.totalMilestones + total,
      };
    },
    { completedMilestones: 0, totalMilestones: 0 },
  );

  const getGreetingKey = () => {
    const hour = new Date().getHours();
    const values = { name: user.firstname };
    if (hour < 12) return t('shared.home.userGreeting.greeting.morning', values);
    if (hour < 18) return t('shared.home.userGreeting.greeting.afternoon', values);
    return t('shared.home.userGreeting.greeting.evening', values);
  };

  return (
    <Stack className='grow gap-2 w-full'>
      <span className='typography-display2 text-primary-100'>{getGreetingKey()}</span>
      <span className='typography-body1 text-text-secondary'>
        <Trans
          i18nKey='shared.home.userGreeting.description'
          values={{ activeWorkstreams, completedMilestones, totalMilestones }}
          components={{ bold: <span className='font-bold text-text-primary' /> }}
        />
      </span>
    </Stack>
  );
};
